// hidden-runner — a second, OFF-SCREEN instance of the existing isolated-surface runner harness,
// plus a per-call timeout wrapper (find-the-bug-activity, Trello C902VeMO).
//
// REUSABLE ON PURPOSE. Nothing here knows about find-the-bug: the separately-scoped Testing
// module card (Trello 2P8y4y9S) needs the same two mechanisms, and imports this module rather
// than rebuilding them.
//
// ── Why a second instance ─────────────────────────────────────────────────────────────────
// The runner's public API (`requestFunctionCall` / `requestConsoleRun`) only ever runs the LIVE
// EDITOR BUFFER and streams stdout into the VISIBLE console. Running hidden source through the
// visible instance would either flash that source on screen (a buffer swap) or print its output
// into the student's console. So this builds a SECOND `createEditorCore` — the pure factory
// `editor-core.js` already is — with its own `/runner/` iframe and its own Ace mount, both inside
// a container that is off-screen, zero-size, `aria-hidden` and `inert`. It has no console, no
// status line and no buttons, so nothing it does is ever rendered. `editor-core.js` and
// `editor-api.js` are NOT modified and the postMessage contract is unchanged: this is the
// existing runner, instantiated twice. The iframe URL starts with the `/runner/` isolation
// prefix, so COOP/COEP apply exactly as they do to the visible frame.
//
// ── Why a per-call timeout ────────────────────────────────────────────────────────────────
// The only existing timeout in the codebase is Code Challenge's whole-check watchdog, and a
// batched `requestFunctionCall` runs every case in one warm exec — so one non-terminating case
// would sink every result. `runWithTimeout` bounds ONE call: if it has not resolved within the
// budget the runner is STOPPED and then RESTARTED (a fresh iframe, the same force-kill the
// Reload control uses) and the caller is told `timeout`. The restart is unconditional after a
// timeout because a stopped call can still post a late result, and editor-core resolves the
// NEXT pending request with whatever result arrives first — restarting is what guarantees a late
// answer can never be credited to the following row. It costs a Pyodide reload, only on an
// actual timeout.

import { createEditorCore } from './editor-core.js';

var TERMINAL_STATES = ['finished', 'stopped', 'student-error', 'runner-error'];

export var DEFAULT_CALL_TIMEOUT_MS = 2000;
var READY_TIMEOUT_MS = 45000;
// How long a request may spend BOOTING (fresh worker + Pyodide init) before the program starts
// running. Generous: a slow network or a cold cache is not the student's infinite loop.
var BOOT_TIMEOUT_MS = 30000;

/**
 * Resolve when `core` next emits `ready`, or reject after `ms`. Never leaves a listener behind.
 */
export function waitForReady(core, ms) {
  return new Promise(function (resolve, reject) {
    var timer = null;
    function onReady() {
      if (timer) { clearTimeout(timer); timer = null; }
      core.off('ready', onReady);
      resolve();
    }
    core.on('ready', onReady);
    timer = setTimeout(function () {
      timer = null;
      core.off('ready', onReady);
      reject(new Error('runner-ready-timeout'));
    }, (typeof ms === 'number') ? ms : READY_TIMEOUT_MS);
  });
}

/**
 * Force-kill and wait for the runner to come back. `restart()` re-navigates the iframe; the
 * runner re-emits `ready` and editor-core re-stages its live buffer.
 */
export function recoverRunner(core, readyMs) {
  try { core.stop(); } catch (e) { /* a stop on a wedged frame may throw; the restart is the fix */ }
  var ready = waitForReady(core, readyMs);
  core.restart();
  return ready;
}

/**
 * Run ONE runner request with a host-side time budget.
 *
 *   start   — a function returning the request's promise (e.g.
 *             `function () { return core.requestFunctionCall(calls); }`). Runner requests never
 *             reject by contract; a harness failure shows up as a `runner-error` state instead.
 *   ms      — the budget (default 2000), counted from the runner's `running` state — i.e. the
 *             program's own execution time, never the per-run worker/Pyodide boot.
 *   opts    — { readyMs } the restart allowance; { bootMs } how long the request may stay
 *             booting before it counts as a timeout (default 30000).
 *
 * Resolves (never rejects) with:
 *   { status: 'ok', value }       the request answered in time
 *   { status: 'timeout' }         it did not; the runner has been stopped, restarted and is ready
 *   { status: 'error' }           the runner itself failed (`runner-error`); it has been restarted
 *   { status: 'lost' }            the restart after a timeout/error did not come back in time
 */
export function runWithTimeout(core, start, ms, opts) {
  var budget = (typeof ms === 'number' && ms > 0) ? ms : DEFAULT_CALL_TIMEOUT_MS;
  var readyMs = opts && typeof opts.readyMs === 'number' ? opts.readyMs : READY_TIMEOUT_MS;
  var bootMs = opts && typeof opts.bootMs === 'number' ? opts.bootMs : BOOT_TIMEOUT_MS;
  return new Promise(function (resolve) {
    var settled = false;
    var timer = null;
    var armed = false;

    function cleanup() {
      if (timer) { clearTimeout(timer); timer = null; }
      core.off('state', onState);
    }
    function settleAfterRecovery(status) {
      if (settled) { return; }
      settled = true;
      cleanup();
      recoverRunner(core, readyMs).then(function () {
        resolve({ status: status });
      }, function () {
        resolve({ status: 'lost' });
      });
    }
    function arm(delay) {
      if (timer) { clearTimeout(timer); }
      timer = setTimeout(function () {
        timer = null;
        settleAfterRecovery('timeout');
      }, delay);
    }
    function onState(state) {
      if (state === 'runner-error') { settleAfterRecovery('error'); return; }
      // The runner boots a FRESH worker (and re-initialises Pyodide, ~2-4 s) for EVERY run, so
      // the per-call budget bounds the PROGRAM's execution: it starts when the runner reports
      // `running`, not when the request is posted. Until then only the boot allowance applies.
      if (state === 'running' && !armed) { armed = true; arm(budget); }
    }

    core.on('state', onState);
    arm(bootMs);

    var p;
    try { p = start(); } catch (e) { settleAfterRecovery('error'); return; }
    Promise.resolve(p).then(function (value) {
      if (settled) { return; }   // a late answer after a timeout is discarded, never credited
      settled = true;
      cleanup();
      resolve({ status: 'ok', value: value });
    });
  });
}

/** True when a state string is terminal (exported for callers that watch runs themselves). */
export function isTerminalState(state) {
  return TERMINAL_STATES.indexOf(state) !== -1;
}

/**
 * Build the off-screen runner.
 *
 *   opts.doc        the page document
 *   opts.ace        the Ace module the page already bundles (editor-core requires one)
 *   opts.runnerSrc  the runner URL — read from the visible `#runner-frame[data-runner-src]` by
 *                   default, so both instances load the identical, isolation-prefixed runner
 *   opts.mode       the runner language ('python' for console Python)
 *   opts.source     the hidden program (never rendered)
 *
 * Returns { ready, setSource, callFunction, consoleRun, runSource, destroy, core }.
 *   ready()                         Promise — resolves once the runner is up, rejects on failure
 *   setSource(src)                  replace the hidden program
 *   callFunction(name, args, ms)    one function call against the hidden program, bounded
 *   consoleRun(stdinLines, ms)      one canned-stdin run of the hidden program, bounded
 *   runSource(src, stdinLines, ms)  run OTHER source once (e.g. a parse helper), then restore
 *                                   the hidden program; bounded
 */
export function createHiddenRunner(opts) {
  var o = opts || {};
  var doc = o.doc || document;
  var visibleFrame = doc.getElementById('runner-frame');
  var runnerSrc = o.runnerSrc
    || (visibleFrame && (visibleFrame.getAttribute('data-runner-src') || visibleFrame.getAttribute('src')))
    || '/runner/index.html';
  var mode = o.mode || 'python';
  var source = typeof o.source === 'string' ? o.source : '';

  // Off-screen, zero-size, inert, hidden from assistive tech. `inert` also keeps the hidden Ace
  // textarea out of the tab order.
  var host = doc.createElement('div');
  host.className = 'hidden-runner-host';
  host.setAttribute('aria-hidden', 'true');
  host.setAttribute('inert', '');
  host.style.cssText = 'position:absolute;left:-10000px;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
  var mount = doc.createElement('div');
  mount.style.cssText = 'width:1px;height:1px;';
  var frame = doc.createElement('iframe');
  frame.setAttribute('title', 'Hidden runner');
  frame.setAttribute('tabindex', '-1');
  frame.setAttribute('data-runner-src', runnerSrc);
  frame.style.cssText = 'width:1px;height:1px;border:0;';
  host.appendChild(mount);
  host.appendChild(frame);
  doc.body.appendChild(host);

  var core = createEditorCore({ ace: o.ace, frame: frame, mount: mount, mode: mode });
  var readyPromise = waitForReady(core, o.readyMs);
  core.load({ source: source, mode: mode });

  // Serialise every request: editor-core holds ONE pending resolver per request kind.
  var queue = Promise.resolve();
  function enqueue(fn) {
    var next = queue.then(fn, fn);
    queue = next.then(function () {}, function () {});
    return next;
  }

  function setSource(src) {
    source = typeof src === 'string' ? src : '';
    core.load({ source: source, mode: mode });
  }

  return {
    core: core,
    ready: function () { return readyPromise; },
    setSource: function (src) { return enqueue(function () { setSource(src); }); },
    callFunction: function (name, args, ms) {
      return enqueue(function () {
        return runWithTimeout(core, function () {
          return core.requestFunctionCall([{ functionName: name, args: Array.isArray(args) ? args : [] }]);
        }, ms);
      });
    },
    consoleRun: function (stdinLines, ms) {
      return enqueue(function () {
        return runWithTimeout(core, function () {
          return core.requestConsoleRun(Array.isArray(stdinLines) ? stdinLines : []);
        }, ms);
      });
    },
    runSource: function (src, stdinLines, ms) {
      return enqueue(function () {
        var keep = source;
        core.load({ source: typeof src === 'string' ? src : '', mode: mode });
        return runWithTimeout(core, function () {
          return core.requestConsoleRun(Array.isArray(stdinLines) ? stdinLines : []);
        }, ms).then(function (res) {
          setSource(keep);
          return res;
        });
      });
    },
    destroy: function () {
      try { core.destroy(); } catch (e) { /* ignore */ }
      if (host.parentNode) { host.parentNode.removeChild(host); }
    }
  };
}
